import React, { useEffect, useState, forwardRef } from 'react';
import {
  Wand2,
  Pencil,
  Check,
  Wrench,
  Repeat,
  Trash2,
  X,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

import Clock from 'react-live-clock';
import { NextIcon, PrevIcon, RestartIcon } from '../Icons';
import { useSelector } from 'react-redux';
import * as Theme from '../theme/theme';
import axios from 'axios';
import Links from './SocialNetworkIcons/Links';
import {
  API_POSTS_EDIT,
  API_REGEN_AI_POST_CONTENT,
  API_EDIT_PROMPT
} from '../utils';
import ImagesCarousel from './ImagesCarousel';

import { postsSelector } from '../store/slices/posts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';

const ViewPostsInCalendar = forwardRef((props, ref) => {
  const status = [
    { title: 'Schedule' },
    { title: 'Posted' },
    { title: 'Draft' },
    { title: 'Prompt' }
  ];
  const [statePost, setStatePost] = useState(0);
  const [facebook, setFacebook] = useState([]);
  const [googleMyBusiness, setGoogleMyBusiness] = useState([]);
  const [twitter, setTwitter] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [facebookResponse, setFacebookResponse] = useState([]);
  const [gmbResponse, setGmbResponse] = useState([]);
  const [twResponse, setTwResponse] = useState([]);
  const [igResponse, setIgResponse] = useState([]);
  const [tiktok, setTiktok] = useState(false);
  const [tiktokResponse, setTiktokResponse] = useState(false);
  const [fbDisplay, setFbDisplay] = useState([]);
  const [gmbDisplay, setGmbDisplay] = useState([]);
  const [twDisplay, setTwDisplay] = useState([]);
  const [editableMessage, setEditableMessage] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [output, setOutput] = useState('');
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteFbChecked, setDeleteFbChecked] = useState(false);
  const [deleteGmbChecked, setDeleteGmbChecked] = useState(false);
  const [deleteTwChecked, setDeleteTwChecked] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const currentEmail = useSelector((state) => state?.auth?.userInfo?.email);
  const { user } = useUser();
  const hasProPlan = user?.publicMetadata?.plan === 'pro';

  const { selectedPost } = useSelector(postsSelector);

  useEffect(() => {
    if (!props?.post?.status) return;
    if (props.post.status === 'Draft') {
      setStatePost(2);
    } else if (props.post.status === 'Prompt') {
      setStatePost(3);
      return;
    } else {
      // TIME ZONE
      if (typeof props.post.start === 'number') {
        new Date(props.timeZone) >= new Date(props.post.start * 1000)
          ? setStatePost(1)
          : setStatePost(0);
      } else {
        new Date(props.timeZone) >= props.post.start
          ? setStatePost(1)
          : setStatePost(0);
      }
    }

    //SET SOCIAL NETWORKS
    setGoogleMyBusiness(props.post?.socialNetworks.googleMyBusiness);
    setTwitter(props.post?.socialNetworks.twitter);
    setFacebook(props.post?.socialNetworks.facebook);
    setInstagram(props.post?.socialNetworks.instagram);
    setTiktok(props.post?.socialNetworks.tiktok || false);
    //SET RESPONSES
    setFacebookResponse(props.post?.socialNetworks.facebookPosted);
    setGmbResponse(props.post?.socialNetworks.googleMyBusinessPosted);
    setTwResponse(props.post?.socialNetworks.twitterPosted);
    setIgResponse(props.post?.socialNetworks.instagramPosted);
    setTiktokResponse(
      props.post?.socialNetworks.tiktokPosted ||
        props.post?.tiktok?.status === 'PUBLISHED' ||
        false
    );

    facebook ? setFbDisplay('inline-block') : setFbDisplay('hidden');
    googleMyBusiness ? setGmbDisplay('inline-block') : setGmbDisplay('hidden');
    twitter ? setTwDisplay('inline-block') : setTwDisplay('hidden');
  }, [
    props?.post?.id,
    props?.refresh,
    props?.posts,
    props?.post,
    facebook,
    googleMyBusiness,
    props?.timeZone,
    twitter
  ]);

  useEffect(() => {
    if (!editableMessage) {
      setEditedMessage(props?.post?.title);
    }
  }, [editableMessage]);

  useEffect(() => {
    setOutput(null);
  }, [props?.post?.id]);

  // Keyboard navigation
  useEffect(() => {
    if (!props.show) return;

    const handleKeyPress = (event) => {
      // Don't trigger navigation if editing
      if (isEditingMessage || editableMessage) return;

      if (event.key === 'ArrowLeft' && props.post && getIdxPost() > 0) {
        event.preventDefault();
        props.carrouselModal(0);
      } else if (
        event.key === 'ArrowRight' &&
        props.post &&
        getIdxPost() < props.posts.length - 1
      ) {
        event.preventDefault();
        props.carrouselModal(1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    props.show,
    props.post,
    props.posts,
    isEditingMessage,
    editableMessage,
    props.carrouselModal
  ]);

  const regenPostContent = async () => {
    setIsEditingMessage(true);
    let start;
    if (typeof props.post.start === 'object') {
      start = new Date(props.post.start).getTime();
      start = start / 1000;
      start = Math.floor(start);
    } else {
      start = props.post.start;
    }
    const regenResponse = await axios.post(API_REGEN_AI_POST_CONTENT, {
      clientId: props?.post?.client?.id,
      unixTimestamp: String(start)
    });
    setOutput(regenResponse.data.body);
    setIsEditingMessage(false);
  };

  const handleEditMessage = async () => {
    setIsEditingMessage(true);
    const response = await axios.post(API_EDIT_PROMPT, {
      clientId: props?.post?.client?.id,
      prompt: editedMessage,
      editedBy: props?.userRole?.displayName,
      SK: props?.post?.SK
    });
    const { data } = response;
    await props.carrouselModal(8, data?.id);

    await regenPostContent();

    setIsEditingMessage(false);
    setEditableMessage(!editableMessage);
  };

  function formatDayWeek(input) {
    if (!input) return;
    const [day, week] = input.split('#');

    const weekDays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday'
    ];

    const dayNumber = parseInt(day, 10);
    const weekNumber = parseInt(week, 10);

    if (dayNumber >= 1 && dayNumber <= 7 && weekNumber > 0) {
      const dayName = weekDays[dayNumber - 1];
      const weekOrdinal = getWeekOrdinal(weekNumber);
      const result = `${dayName} of the ${weekOrdinal} week`;
      return result;
    } else {
      return 'Invalid argument';
    }
  }

  function getWeekOrdinal(weekNumber) {
    if (weekNumber === 1) {
      return '1st';
    } else if (weekNumber === 2) {
      return '2nd';
    } else if (weekNumber === 3) {
      return '3rd';
    } else {
      return weekNumber + 'th';
    }
  }

  const getTimeZoneClock = (currentClientTimeZone) => {
    let timeZoneClock = null;
    switch (currentClientTimeZone) {
      case 'Eastern Daylight Time - (GMT-4)':
        timeZoneClock = 'America/New_York';
        break;
      case 'Central Daylight Time - (GMT-5)':
        timeZoneClock = 'America/Chicago';
        break;
      case 'Mountain Daylight Time - (GMT-6)':
        timeZoneClock = 'America/Denver';
        break;
      case 'Mountain Standard Time - (GMT-7)':
        timeZoneClock = 'America/Phoenix';
        break;
      case 'Pacific Daylight Time - (GMT-7)':
        timeZoneClock = 'America/Los_Angeles';
        break;
      case 'Alaska Daylight Time - (GMT-8)':
        timeZoneClock = 'America/Anchorage';
        break;
      default:
        timeZoneClock = 'Pacific/Honolulu';
        break;
    }
    return timeZoneClock;
  };

  function formatDate(date) {
    if (typeof date == 'number') {
      let d = new Date(date * 1000);

      const tf = new Intl.DateTimeFormat('en', {
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        dayPeriod: 'short'
      });

      try {
        const [
          { value: mo },
          ,
          { value: da },
          ,
          { value: ho },
          ,
          { value: mi },
          ,
          { value: dp }
        ] = tf.formatToParts(d);
        return `${da} ${mo} - ${ho} ${mi} ${dp}`;
      } catch (error) {
        return 'undefined';
      }
    }

    const dtf = new Intl.DateTimeFormat('en', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      dayPeriod: 'short'
    });

    try {
      const [
        { value: mo },
        ,
        { value: da },
        ,
        { value: ye },
        ,
        { value: ho },
        ,
        { value: mi },
        ,
        { value: dp }
      ] = dtf.formatToParts(date);
      return `${da} ${mo} ${ye} - ${ho} ${mi} ${dp}`;
    } catch (error) {
      return 'undefined';
    }
  }

  let currentRole = props.userRole.role;

  let getIdxPost = () => {
    let idx = 0;
    while (props?.posts[idx]?.id !== props?.post?.id && idx < 300) {
      idx++;
    }
    return idx;
  };

  const headerColor =
    statePost === 0 &&
    props?.post?.createdBy !== 'APP' &&
    props?.post?.status !== 'Promo'
      ? '#039be5'
      : statePost === 1 &&
        props?.post?.createdBy !== 'APP' &&
        props?.post?.status !== 'Promo'
      ? '#f9b47f'
      : statePost === 2 &&
        props?.post?.createdBy !== 'APP' &&
        props?.post?.status !== 'Promo'
      ? '#7987a1'
      : statePost === 3
      ? Theme.promptColor
      : props?.post?.createdBy === 'APP'
      ? '#10b759'
      : props?.post?.status === 'Promo'
      ? Theme.promoColor
      : '#7987a1';

  return (
    <>
      <Dialog
        open={props.show}
        onOpenChange={(open) => !open && props.onClose()}>
        <DialogContent className="p-0 gap-0 max-w-lg overflow-hidden rounded-2xl border-0 [&>button]:hidden">
          {/* Header bar */}
          <div
            className="flex items-center justify-between gap-4 px-6 py-3"
            style={{ backgroundColor: headerColor }}>
            {/* Navigation buttons - Left side */}
            <div className="flex items-center gap-2">
              {props.show &&
                props.post &&
                getIdxPost() > 0 &&
                !isEditingMessage &&
                !editableMessage && (
                  <button
                    type="button"
                    className="text-white hover:opacity-80 transition-opacity"
                    onClick={() => {
                      props.carrouselModal(0);
                    }}
                    aria-label="Previous post">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
              {props.show &&
                props.post &&
                getIdxPost() < props.posts.length - 1 &&
                !isEditingMessage &&
                !editableMessage && (
                  <button
                    type="button"
                    className="text-white hover:opacity-80 transition-opacity"
                    onClick={() => {
                      props.carrouselModal(1);
                    }}
                    aria-label="Next post">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
            </div>

            {/* Action buttons - Right side */}
            <nav className="flex items-center gap-4">
              {hasProPlan && (statePost === 2 || statePost === 0) && (
                <button
                  className="text-white hover:opacity-80 transition-opacity"
                  onClick={() => {
                    props.updatePost(props?.post?.id, {
                      isDraft: statePost === 2 ? false : true
                    });
                    props.close();
                  }}>
                  <Wand2 className="h-5 w-5" />
                </button>
              )}
              {hasProPlan &&
                (statePost === 1
                  ? []
                  : !isEditingMessage && (
                      <button
                        className="text-white hover:opacity-80 transition-opacity"
                        disabled={isEditingMessage}
                        onClick={() => {
                          if (statePost === 3) {
                            setEditableMessage(!editableMessage);
                            if (editableMessage) handleEditMessage();
                            return;
                          }

                          props.editPost(props.post);
                          props.close();
                        }}>
                        {!editableMessage ? (
                          <Pencil className="h-5 w-5" />
                        ) : (
                          <Check className="h-5 w-5" />
                        )}
                      </button>
                    ))}
              {hasProPlan && statePost === 1 ? (
                <button
                  className="text-white hover:opacity-80 transition-opacity"
                  onClick={() => {
                    props.repairPost(props.post);
                    props.close();
                  }}>
                  <Wrench className="h-5 w-5" />
                </button>
              ) : null}
              {hasProPlan && statePost === 1 && (
                <button
                  className="text-white hover:opacity-80 transition-opacity"
                  onClick={() => {
                    props.rePost(props.post);
                    props.close();
                  }}>
                  <Repeat className="h-5 w-5" />
                </button>
              )}
              {hasProPlan && (statePost === 0 || statePost === 1) && (
                <button
                  className="text-white hover:opacity-80 transition-opacity"
                  onClick={() => {
                    props.getStatusPost();
                    props.carrouselModal(7);
                  }}>
                  <RefreshCw className="h-5 w-5" />
                </button>
              )}
              {hasProPlan &&
                (statePost === 1 ? (
                  <button
                    disabled={isEditingMessage}
                    className="text-white hover:opacity-80 transition-opacity disabled:opacity-50"
                    onClick={() => {
                      setDeleteFbChecked(true);
                      setDeleteGmbChecked(true);
                      setDeleteTwChecked(true);
                      setDeleteConfirmModalOpen(true);
                    }}>
                    <Trash2 className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    className="text-white hover:opacity-80 transition-opacity disabled:opacity-50"
                    disabled={isEditingMessage}
                    onClick={() => {
                      setDeleteConfirmModalOpen(true);
                    }}>
                    <Trash2 className="h-5 w-5" />
                  </button>
                ))}
              <button
                className="text-white hover:opacity-80 transition-opacity disabled:opacity-50"
                disabled={isEditingMessage}
                onClick={() => {
                  props.onClose && props.onClose();
                  props.close();
                }}>
                <X className="h-5 w-5" />
              </button>
            </nav>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Date & Time row */}
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                  {statePost !== 3 ? 'Date' : 'scheduled for'}
                </p>
                <p className="text-base font-medium text-foreground mt-1">
                  {statePost !== 3
                    ? formatDate(props?.post?.start)
                    : 'Each ' + formatDayWeek(props?.post?.SK)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                  {statePost !== 3 ? 'Current Time' : 'modified by'}
                </p>
                <p className="text-base font-medium text-foreground mt-1">
                  {statePost !== 3 ? (
                    <Clock
                      format={'HH:mm:ss'}
                      ticking={true}
                      timezone={getTimeZoneClock(props.time)}
                    />
                  ) : props?.post?.editedBy ? (
                    props.post?.editedBy
                  ) : (
                    '--'
                  )}
                </p>
              </div>
            </div>

            {/* Created By / Content Strategy */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                {statePost !== 3 ? 'Created By' : 'content strategy'}
              </p>
              <p className="text-base font-medium text-foreground mt-1">
                {statePost !== 3 && props?.post?.createdBy
                  ? `${props?.post?.createdBy}`
                  : statePost !== 3
                  ? ''
                  : props?.contentStrategy?.strategyName || '--'}
              </p>
            </div>

            {/* Created At & Status */}
            <div className="flex justify-between items-start">
              {statePost !== 3 ? (
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                    Created At
                  </p>
                  <p className="text-base font-medium text-foreground mt-1">
                    {props?.post?.createdAt
                      ? `${formatDate(props?.post?.createdAt)}`
                      : ''}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                    Modified at
                  </p>
                  <p className="text-base font-medium text-foreground mt-1">
                    {props?.post?.updatedAt
                      ? formatDate(props.post?.updatedAt / 1000)
                      : '--'}
                  </p>
                </div>
              )}
              <div>
                <label
                  style={{ color: headerColor }}
                  className="text-sm font-semibold uppercase tracking-wide">
                  {status[statePost]['title']}*
                </label>
              </div>
            </div>

            {/* Description / Prompt */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                {statePost !== 3 ? (
                  'Description'
                ) : (
                  <>
                    Prompt
                    {editableMessage && !isEditingMessage ? (
                      <button
                        className="ml-2 inline-flex items-center text-gray-600 hover:text-gray-800"
                        style={{
                          border: 'none',
                          background: 'none',
                          outline: 'none'
                        }}
                        onClick={() => {
                          handleEditMessage();
                        }}>
                        <Check size={20} />
                      </button>
                    ) : null}
                    {isEditingMessage && (
                      <span className="ml-2 inline-flex items-center">
                        <Loader2 size={20} className="animate-spin" />
                      </span>
                    )}
                  </>
                )}
              </p>
              {statePost !== 3 ? (
                <p className="text-base text-foreground mt-1">
                  {props?.post?.title}
                </p>
              ) : (
                <textarea
                  type="text"
                  disabled={!editableMessage || isEditingMessage}
                  placeholder="Enter your post message here"
                  value={editableMessage ? editedMessage : props?.post?.title}
                  className="w-full mt-1 p-2 text-base text-foreground rounded-md"
                  style={{
                    minHeight: '125px',
                    maxHeight: '300px',
                    resize: editableMessage ? 'vertical' : 'none',
                    border: editableMessage ? '1px solid #ced4da' : 'none',
                    backgroundColor: 'inherit'
                  }}
                  onChange={(e) => {
                    setEditedMessage(e.target.value);
                  }}
                />
              )}
            </div>

            {/* Output (only for prompts) */}
            {statePost === 3 && (
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                  output{' '}
                  {output && (
                    <button
                      className="ml-2 inline-flex items-center text-gray-600 hover:text-gray-800"
                      style={{
                        border: 'none',
                        background: 'none',
                        outline: 'none'
                      }}
                      disabled={isEditingMessage}
                      onClick={async () => {
                        await regenPostContent();
                      }}>
                      <RefreshCw size={16} />
                    </button>
                  )}
                </p>
                <p className="text-base text-foreground mt-1">
                  {output ? JSON.parse(output)?.message : '--'}
                </p>
              </div>
            )}

            {/* Video preview for TikTok posts */}
            {statePost !== 3 && tiktok && props.post?.tiktok?.videoUrl && (
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                  Video
                </p>
                <div className="mt-2 flex justify-center">
                  <div className="w-36 rounded-xl overflow-hidden bg-black border border-border">
                    <video
                      src={`${props.post.tiktok.videoUrl}#t=0.1`}
                      preload="metadata"
                      controls
                      playsInline
                      className="w-full aspect-[9/16] object-contain"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Image (non-TikTok posts) */}
            {statePost !== 3 &&
              !tiktok &&
              selectedPost?.images !== undefined && (
                <div>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                    Image
                  </p>
                  <div className="mt-2 flex justify-center">
                    {selectedPost?.images?.length > 1 ? (
                      <ImagesCarousel images={selectedPost.images} />
                    ) : (
                      <div className="w-48 h-48 rounded-lg bg-muted/50 border border-border flex items-center justify-center overflow-hidden">
                        <img
                          src={props?.post?.images[0]['url']}
                          alt="Post"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <Button
              variant="default"
              size="sm"
              className="rounded-lg px-6"
              disabled={isEditingMessage}
              onClick={() => {
                props.onClose && props.onClose();
                props.close();
              }}>
              Close
            </Button>
            {statePost === 3 && output && (
              <Button
                variant="default"
                size="sm"
                className="rounded-lg px-6 bg-warning hover:bg-warning/90"
                disabled={isEditingMessage}
                onClick={() => {
                  axios
                    .post(API_POSTS_EDIT, output)
                    .then((response) => {
                      props.close();
                      props.handleShowPrompts();
                    })
                    .catch((error) => {
                      if (error) {
                        setErrorModalOpen(true);
                      }
                      console.error(error);
                    });
                }}>
                Confirm
              </Button>
            )}
            {statePost !== 3 && (
              <div className="flex items-center gap-3">
                <Links
                  selectedClient={props.selectedClient}
                  userInfo={props.userRole}
                  facebook={facebook}
                  facebookResponse={facebookResponse}
                  googleMyBusiness={googleMyBusiness}
                  gmbResponse={gmbResponse}
                  twitter={twitter}
                  twResponse={twResponse}
                  instagram={instagram}
                  igResponse={igResponse}
                  tiktok={tiktok}
                  tiktokResponse={tiktokResponse}
                  statePost={statePost}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmModalOpen}
        onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent className="sm:max-w-md z-[99999]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Delete Post
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center">
              Are you sure you want to delete this post?
            </DialogDescription>
          </DialogHeader>
          {statePost === 1 && (
            <div className="space-y-3 py-4">
              {fbDisplay !== 'hidden' && (
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="deleteFbCheckbox"
                    checked={deleteFbChecked}
                    onChange={(e) => setDeleteFbChecked(e.target.checked)}
                    className="w-4 h-4 accent-primary cursor-pointer"
                  />
                  <label
                    htmlFor="deleteFbCheckbox"
                    className="flex items-center cursor-pointer">
                    <span
                      className="fab fa-fw fa-facebook mr-2"
                      style={{ color: '#3b5998', fontSize: '20px' }}
                    />
                    <span className="text-sm">Delete from Facebook</span>
                  </label>
                </div>
              )}
              {gmbDisplay !== 'hidden' && (
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="deleteGmbCheckbox"
                    checked={deleteGmbChecked}
                    onChange={(e) => setDeleteGmbChecked(e.target.checked)}
                    className="w-4 h-4 accent-primary cursor-pointer"
                  />
                  <label
                    htmlFor="deleteGmbCheckbox"
                    className="flex items-center cursor-pointer">
                    <span
                      className="fab fa-fw fa-google mr-2"
                      style={{ color: '#4284f5', fontSize: '20px' }}
                    />
                    <span className="text-sm">
                      Delete from Google My Business
                    </span>
                  </label>
                </div>
              )}
              {twDisplay !== 'hidden' && (
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="deleteTwCheckbox"
                    checked={deleteTwChecked}
                    onChange={(e) => setDeleteTwChecked(e.target.checked)}
                    className="w-4 h-4 accent-primary cursor-pointer"
                  />
                  <label
                    htmlFor="deleteTwCheckbox"
                    className="flex items-center cursor-pointer">
                    <span
                      className="fab fa-fw fa-twitter mr-2"
                      style={{ color: '#1da1f2', fontSize: '20px' }}
                    />
                    <span className="text-sm">Delete from Twitter</span>
                  </label>
                </div>
              )}
              {tiktok && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-muted-foreground">
                    TikTok posts cannot be deleted remotely from AdlerLocal.
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-center gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmModalOpen(false)}
              className="h-11 px-8">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (statePost === 1) {
                  const deleteData = {
                    fbCheck: deleteFbChecked,
                    gmbCheck: deleteGmbChecked,
                    twCheck: deleteTwChecked
                  };
                  props.deletePost(props.post.id, deleteData);
                } else {
                  props.deletePost(props.post.id, null);
                }
                setDeleteConfirmModalOpen(false);
                props.close();
              }}
              className="h-11 px-8">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="sm:max-w-md z-[99999]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Error
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center">
              We have trouble scheduling the publication
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => setErrorModalOpen(false)}
              className="h-11 px-8">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default ViewPostsInCalendar;
